/**
 * LeagueListPage — /leagues
 * ─────────────────────────────────────────────────────────────────
 * Entry point for the League Module surface. Lists every league
 * the current user is a member of (any role) and lets any
 * authenticated user create a new one (they become the manager).
 *
 * Phase 2a — first page in the league UI. Subsequent league pages
 * (Dashboard, Members, Settings) live under /league/:leagueId/...
 * and share the LeagueSubNav component for in-page navigation.
 *
 * 2026-09-28 — super admins also get the League demo launcher here
 * (start / open / rebuild / remove the simulated league in one click);
 * the full options and presenter links stay on Admin → Demo.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Trophy, Plus, X, Crown, Shield, Eye, Lock, Users, MessageSquare, ExternalLink,
  FlaskConical, PlayCircle, RefreshCw, Trash2, ListChecks,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext.jsx';

// Visual treatment per league role. Used in the role badge column.
// Manager = gold crown, Administrator = ice shield, Viewer = grey eye.
const ROLE_META = {
  manager:       { Icon: Crown,  color: 'var(--puck)',     label: 'Manager' },
  administrator: { Icon: Shield, color: 'var(--ice)',      label: 'Administrator' },
  viewer:        { Icon: Eye,    color: 'var(--text-2)',   label: 'Viewer' },
  // 2026-09-28 — not a league member, but coaches a team enrolled in
  // one of its seasons (GET /api/leagues lists these too).
  team_coach:    { Icon: Users,  color: 'var(--goal)',     label: 'Team coach' },
};

export default function LeagueListPage() {
  const navigate = useNavigate();

  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create modal state. Keep it inline (single modal) — leagues
  // are created rarely and the form is short (name + optional slug).
  const [showCreate, setShowCreate]   = useState(false);
  const [newName, setNewName]         = useState('');
  const [newSlug, setNewSlug]         = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating]       = useState(false);

  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const [reloadKey, setReloadKey] = useState(0);   // bump to re-fetch (demo built/removed)

  useEffect(() => {
    let cancelled = false;
    api.get('/leagues')
      .then(({ data }) => {
        if (cancelled) return;
        setLeagues(data?.leagues || []);
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 is handled by the api interceptor (refresh + redirect to
        // login). 404 means the user doesn't have the 'leagues'
        // feature flag set — silently redirect to dashboard rather
        // than confuse them with an error toast. (v2.5.118)
        if (err?.response?.status === 404) {
          navigate('/dashboard', { replace: true });
          return;
        }
        if (err?.response?.status !== 401) {
          toast.error('Could not load leagues');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [navigate, reloadKey]);

  async function handleCreate() {
    const name = newName.trim();
    if (name.length < 2) {
      toast.error('League name must be at least 2 characters');
      return;
    }
    setCreating(true);
    try {
      const body = { name };
      if (newSlug.trim()) body.slug = newSlug.trim();
      if (newDescription.trim()) body.description = newDescription.trim();
      const { data } = await api.post('/leagues', body);
      const created = data?.league;
      if (!created?.id) throw new Error('Backend returned no league id');
      toast.success(`Created "${created.name}"`);
      // Optimistic: append to list and navigate immediately so the
      // user lands on their new league. The list will re-fetch next
      // time they come back to /leagues.
      setLeagues((prev) => [...prev, { ...created, member_role: 'manager', permissions_json: {} }]);
      setShowCreate(false);
      setNewName(''); setNewSlug(''); setNewDescription('');
      navigate(`/league/${created.id}/dashboard`);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to create league';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 880 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 className="section-title text-4xl">Leagues</h1>
        <button
          type="button"
          className="btn-ice"
          onClick={() => setShowCreate(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Plus size={16} />
          New League
        </button>
      </div>

      <p style={{ color: 'var(--text-2)', marginBottom: 24, fontSize: 14 }}>
        Leagues sit above teams — schedule games across many teams, allocate players from a shared
        pool, assign referees and scorekeepers, and run multiple seasons.
      </p>

      {isSuperAdmin && <DemoLauncher onChanged={() => setReloadKey((k) => k + 1)} />}

      {loading ? (
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          Loading leagues…
        </div>
      ) : leagues.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {leagues.map((l) => <LeagueRow key={l.id} league={l} />)}
        </div>
      )}

      {showCreate && (
        <CreateLeagueModal
          name={newName} setName={setNewName}
          slug={newSlug} setSlug={setNewSlug}
          description={newDescription} setDescription={setNewDescription}
          creating={creating}
          onCancel={() => { setShowCreate(false); setNewName(''); setNewSlug(''); setNewDescription(''); }}
          onConfirm={handleCreate}
        />
      )}
    </div>
  );
}

// ─── League demo launcher (super admin) ─────────────────────────
// Same backend as Admin → Demo (GET/POST/DELETE /api/admin/demo/league):
// every demo row is flagged is_demo, demo people are never emailed and
// card payments are simulated, so starting and removing it here is safe.
function DemoLauncher({ onChanged }) {
  const { loadUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [unavailable, setUnavailable] = useState(null);
  const [busy, setBusy] = useState(null);           // 'build' | 'remove'

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/demo/league');
      setStatus(data);
      setUnavailable(null);
    } catch (err) {
      if (err?.response?.status === 503) setUnavailable(err.response.data?.error || 'Demo mode is not available on this server yet.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = !!status?.active;
  const run = status?.last_run;
  const summary = active && run?.status === 'active' ? run.summary : null;
  const mainLeagueId = summary?.main_league_id || status?.leagues?.[0]?.id || null;
  const counts = status?.counts || {};

  const build = async () => {
    if (active && !window.confirm('Rebuild the demo? The demo leagues, teams and people are replaced with a fresh set. Real teams and leagues are not touched.')) return;
    setBusy('build');
    try {
      // Rebuild keeps the options last used (here or on Admin → Demo). A
      // first start builds everything the presenter guide walks through:
      // all four scenarios incl. the empty setup league, the public site,
      // and you as a coach of one demo team.
      const options = active && run?.status === 'active' && run.options
        ? run.options
        : { scenarios: { completed: true, in_progress: true, registration: true, blank: true } };
      const { data } = await api.post('/admin/demo/league', options, { timeout: 120000 });
      setStatus(data?.status || null);
      toast.success('Demo league is ready');
      loadUser?.();          // the demo team joins your team switcher
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not build the demo');
      load();
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the demo? Every demo league, team, player, game and registration is deleted. Real teams and leagues are not touched.')) return;
    setBusy('remove');
    try {
      const { data } = await api.delete('/admin/demo/league', { timeout: 120000 });
      setStatus(data?.status || null);
      const r = data?.removed || {};
      toast.success(`Demo removed — ${r.leagues || 0} leagues, ${r.teams || 0} teams, ${r.users || 0} people`);
      loadUser?.();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove the demo');
      load();
    } finally {
      setBusy(null);
    }
  };

  const ghost = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 12px', textDecoration: 'none' };
  return (
    <div className="card" style={{
      marginBottom: 20, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap',
      borderColor: active ? 'var(--goal)' : 'rgba(251,191,36,0.45)',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: active ? 'rgba(52,211,153,0.12)' : 'rgba(251,191,36,0.12)',
      }}>
        <FlaskConical size={20} color={active ? 'var(--goal)' : '#fbbf24'} />
      </div>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {active ? 'League demo is live' : 'League demo'}
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'var(--text-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '1px 6px',
          }}>Super admin</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.5 }}>
          {unavailable ? unavailable
            : busy === 'build' ? 'Building the demo league — about 10 seconds…'
            : busy === 'remove' ? 'Removing the demo…'
            : active ? <>{(status.leagues || []).map((l) => l.name).join(' + ')} · {counts.teams || 0} teams · {counts.users || 0} fictional people. Open it below, or use the presenter links for the full walkthrough.</>
            : 'Build a complete, clearly fictional league to show the whole workflow: a finished season with playoffs, a season in progress with a game tonight, registration open for next season and an empty league to set up from scratch. Nothing touches real teams, and one click removes it.'}
        </div>
        {run?.status === 'failed' && !busy && (
          <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--penalty)' }}>
            The last build failed and was rolled back: {run.error || 'unknown error'}
          </div>
        )}
        {!unavailable && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {!active && (
              <button type="button" className="btn-ice" onClick={build} disabled={!!busy || !status}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <PlayCircle size={15} /> {busy === 'build' ? 'Building…' : 'Start league demo'}
              </button>
            )}
            {active && mainLeagueId && (
              <Link to={`/league/${mainLeagueId}/dashboard`} className="btn-ice"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                <Trophy size={15} /> Open demo league
              </Link>
            )}
            <Link to="/admin?tab=demo" className="btn-ghost" style={ghost}>
              <ListChecks size={14} /> {active ? 'Presenter links' : 'Choose what to simulate'}
            </Link>
            {active && (
              <>
                <button type="button" className="btn-ghost" onClick={build} disabled={!!busy} style={ghost}>
                  <RefreshCw size={14} /> {busy === 'build' ? 'Rebuilding…' : 'Rebuild'}
                </button>
                <button type="button" className="btn-ghost" onClick={remove} disabled={!!busy}
                        style={{ ...ghost, color: 'var(--penalty)' }}>
                  <Trash2 size={14} /> {busy === 'remove' ? 'Removing…' : 'Remove demo'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LeagueRow({ league }) {
  const meta = ROLE_META[league.member_role] || ROLE_META.viewer;
  const Icon = meta.Icon;
  if (league.member_role === 'team_coach') return <CoachedLeagueRow league={league} meta={meta} />;
  return (
    <Link
      to={`/league/${league.id}/dashboard`}
      className="card"
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        textDecoration: 'none', color: 'inherit',
        transition: 'border-color 0.18s, transform 0.18s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: 'var(--ice-a10)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Trophy size={20} color="var(--ice)" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {league.name}
          {/* 2026-09-28 — demo leagues from Admin → Demo. */}
          {league.is_demo && (
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#fbbf24', border: '1px solid rgba(251,191,36,0.5)', borderRadius: 6, padding: '1px 6px',
            }}>Demo</span>
          )}
        </div>
        <div style={{ color: 'var(--text-2)', fontSize: 13, textTransform: 'capitalize' }}>
          {league.sport}{league.is_public ? ' · public page enabled' : ''}
        </div>
      </div>
      <span
        className="badge"
        style={{
          background: 'transparent',
          color: meta.color,
          border: `1px solid ${meta.color}`,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px',
        }}
      >
        <Icon size={12} />
        {meta.label}
      </span>
    </Link>
  );
}

// A league the user coaches in without being a league member: their
// doors in are the coaches' channel (and game sheets from their team's
// schedule) plus the public site.
function CoachedLeagueRow({ league, meta }) {
  const Icon = meta.Icon;
  const teams = Array.isArray(league.via_teams) ? league.via_teams : [];
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: 'var(--goal-a20, rgba(52,211,153,0.15))',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Trophy size={20} color="var(--goal)" />
      </div>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {league.name}
          {league.is_demo && (
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#fbbf24', border: '1px solid rgba(251,191,36,0.5)', borderRadius: 6, padding: '1px 6px',
            }}>Demo</span>
          )}
        </div>
        <div style={{ color: 'var(--text-2)', fontSize: 13 }}>
          Your team{teams.length === 1 ? '' : 's'}: {teams.map(t => t.name).join(', ') || '—'}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <Link to={`/league/${league.id}/chat`} className="btn-ghost"
                style={{ fontSize: 12, padding: '5px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MessageSquare size={12} /> Coaches&apos; channel
          </Link>
          {league.is_public && league.slug && (
            <a href={`/l/${league.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost"
               style={{ fontSize: 12, padding: '5px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ExternalLink size={12} /> League website
            </a>
          )}
        </div>
      </div>
      <span className="badge" style={{
        background: 'transparent', color: meta.color, border: `1px solid ${meta.color}`,
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
      }}>
        <Icon size={12} />
        {meta.label}
      </span>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
      <Trophy size={48} color="var(--text-2)" style={{ margin: '0 auto 16px', opacity: 0.5 }} />
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No leagues yet</div>
      <div style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 20, maxWidth: 440, margin: '0 auto 20px' }}>
        Create your first league to start managing multiple teams under one roof — shared player pool,
        venues, schedules, and standings.
      </div>
      <button type="button" className="btn-ice" onClick={onCreate}>
        <Plus size={16} style={{ marginRight: 8 }} />
        Create your first league
      </button>
    </div>
  );
}

function CreateLeagueModal({ name, setName, slug, setSlug, description, setDescription, creating, onCancel, onConfirm }) {
  // Backdrop closes on click; the dialog itself stops propagation so
  // clicking inside doesn't dismiss.
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, zIndex: 100,
      }}
    >
      {/* Mobile fix: this is a fixed-position overlay modal with no height
          cap. Three text fields (incl. a textarea) can exceed the visible
          area once the on-screen keyboard is up on a short phone; cap at
          90% of the visible height and let the dialog itself scroll rather
          than clipping the Cancel/Create buttons off-screen. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ maxWidth: 480, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 className="section-title text-xl">New League</h2>
          <button
            type="button"
            onClick={onCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: 4 }}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Kitchener Beer Hockey League"
            maxLength={150}
            autoFocus
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            URL slug <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional — auto-derived from name)</span>
          </span>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="kitchener-beer-hockey"
            maxLength={80}
            style={inputStyle}
          />
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
            Used for the public-facing URL once you enable a public page.
          </span>
        </label>

        <label style={{ display: 'block', marginBottom: 24 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            Description <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span>
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A short blurb shown on the league dashboard."
            maxLength={500}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
          />
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={creating}>
            Cancel
          </button>
          <button type="button" className="btn-ice" onClick={onConfirm} disabled={creating || !name.trim()}>
            {creating ? 'Creating…' : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Lock size={14} />
                Create &amp; become manager
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 14px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  outline: 'none',
};
